import { useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button, Stack, Typography } from '@mui/material';

import * as routes from 'router/routes';
import { LABEL_TYPES } from 'constants/components';
import { formatDate } from 'helper/DateLib';
import { useBreakpointFlags } from 'hooks/useBreakpointFlags';
import { useMatchPathParams } from 'hooks/useMatchPathParams';
import { useDialog } from 'hooks/useDialog';
import { dialogPropsSelector } from 'store/entities/ui/slice';
import { useGetMasterDashboardProjectsQuery } from 'store/entities/dashboard/api';
import {
  masterDashboardProjectsGroupSelector,
  masterDashboardProjectsListSelector,
  resetMasterDashboardProjectsAction,
  setMasterDashboardProjectsAction,
} from 'store/entities/dashboard/slice';
import { ColoredTable } from 'components/complex/ColoredTable';
import { Checkbox, Label, SearchInput, Status, TitleTooltip } from 'components/simple';
import { Dialog } from 'components/complex';
import { ButtonConfirmIcon } from 'assets/icons';

const MIN_PROJECTS_COUNT = 2;
const MAX_PROJECTS_COUNT = 1000;

const dialogId = 'create-master_dashboard-dialog';
const styles = {
  dialogPaper: ({ breakpoints }) => ({
    [breakpoints.up('xl')]: {
      maxWidth: 1080,
    },
  }),
  dialogActions: {
    justifyContent: 'flex-end',
  },
  searchInput: ({ breakpoints }) => ({
    [breakpoints.up('sm')]: {
      width: '100%',
    },
  }),
  tableContainer: ({ palette }) => ({
    paddingInline: 1,
    borderRadius: 5,
    border: `1px solid ${palette.colors.darkPale}`,
  }),
  tableCell: {
    height: 76,
    paddingBlock: 2.5,
    '&:first-of-type': {
      borderRadius: '20px 0 0 20px',
    },
    '&:last-of-type': {
      borderRadius: '0 20px 20px 0',
    },
  },
  selectAllButton: {
    paddingBlock: 'unset !important',
  },
  iconWrapper: ({ palette }) => ({
    width: 16,
    height: 16,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 0.5,
    border: `1px solid ${palette.text.primary}`,
    svg: {
      width: 12,
      height: 12,
      color: palette.common.white,
    },
  }),
  iconWrapperSelected: ({ palette }) => ({
    backgroundColor: palette.text.primary,
  }),
  clearSelectionButton: ({ spacing, breakpoints }) => ({
    [breakpoints.up('md')]: {
      padding: spacing(1, 1.5),
    },
  }),
};

const CELL_KEYS = {
  CHECKBOX: 'checkbox',
  NAME: 'name',
  LAUNCH_DATE: 'launch_date',
  PROJECT_SETTINGS: 'project_settings',
  PROJECT_STATUS: 'project_status',
};

const formatHeaders = (headers = []) =>
  headers.map(({ content, ...headerData }) => ({
    ...headerData,
    cellStyles: styles.tableCell,
    content: content ? (
      <Typography variant="body2-bold" color="text.secondary" component="span">
        {content}
      </Typography>
    ) : (
      content
    ),
  }));

export const CreateMasterDashboardDialog = () => {
  const { isXlUp } = useBreakpointFlags();

  const storedProjectsGroup = useSelector(masterDashboardProjectsGroupSelector);
  const storedProjects = useSelector(masterDashboardProjectsListSelector);

  const [searchValue, setSearchValue] = useState('');
  const [selectedProjectsGroup, setSelectedProjectsGroup] = useState(storedProjectsGroup ?? null);
  const [selectedProjects, setSelectedProjects] = useState(storedProjects ?? []);

  const { push } = useHistory();
  const { pathname } = useLocation();
  const { organisationId } = useMatchPathParams();
  const { getResponsiveValueForCurrent } = useBreakpointFlags();

  const dispatch = useDispatch();
  const { closeDialog } = useDialog();
  const props = useSelector(dialogPropsSelector);

  const { data: { projects = [] } = {} } = useGetMasterDashboardProjectsQuery({
    organisation_id: organisationId,
  });

  const filteredProjects = !searchValue
    ? projects
    : projects.filter(({ name = '' }) => name.toLowerCase().includes(searchValue.toLowerCase()));

  const availableProjectsBySelectedGroup =
    !selectedProjectsGroup || selectedProjects.length === MAX_PROJECTS_COUNT
      ? []
      : projects
          .filter(({ id, structureId }) => structureId === selectedProjectsGroup && !selectedProjects.includes(id))
          .slice(0, MAX_PROJECTS_COUNT - selectedProjects.length);

  const isAllProjectsSelected = !!selectedProjectsGroup && !availableProjectsBySelectedGroup.length;

  const onSelectProject = (newValue, { id, structureId }) => {
    const updatedProjects = newValue ? [...selectedProjects, id] : selectedProjects.filter((value) => value !== id);

    setSelectedProjects(updatedProjects);
    setSelectedProjectsGroup((prevState) => (updatedProjects.length ? prevState || structureId : null));
  };

  const onSelectAllProjects = () => {
    if (isAllProjectsSelected) {
      setSelectedProjects([]);
      setSelectedProjectsGroup(null);
    } else {
      setSelectedProjects((prevState) => [...prevState, ...availableProjectsBySelectedGroup.map(({ id }) => id)]);
    }
  };

  const onClearSelection = () => {
    setSelectedProjects([]);
    setSelectedProjectsGroup(null);
  };

  const projectTableHeaders = [
    {
      cellKey: CELL_KEYS.CHECKBOX,
      content: null,
      cellProps: { width: 70 },
    },
    {
      cellKey: CELL_KEYS.NAME,
      content: 'Project Name',
    },
    {
      cellKey: CELL_KEYS.LAUNCH_DATE,
      content: 'Launch Date',
      cellProps: {
        width: !isXlUp ? '15%' : '12%',
        align: 'center',
      },
    },
    {
      cellKey: CELL_KEYS.PROJECT_SETTINGS,
      content: 'Project Settings',
      cellProps: {
        width: '15%',
        align: 'center',
      },
    },
    {
      cellKey: CELL_KEYS.PROJECT_STATUS,
      content: 'Project Status',
      cellProps: {
        width: !isXlUp ? '18%' : '15%',
        align: 'center',
      },
    },
  ];

  const projectTableRows = useMemo(() => {
    const dashboardNameLength = getResponsiveValueForCurrent({
      lg: 30,
      xl: 50,
      xxl: 50,
    });

    return filteredProjects.map(({ id, name, status, launchDate, isPublic, structureId }) => ({
      id,
      cells: [
        {
          cellKey: CELL_KEYS.CHECKBOX,
          cellStyles: styles.tableCell,
          content: (
            <Checkbox
              checked={selectedProjects.includes(id)}
              disabled={
                (!!selectedProjectsGroup && selectedProjectsGroup !== structureId) ||
                (selectedProjects.length === MAX_PROJECTS_COUNT && !selectedProjects.includes(id))
              }
              onChange={(newValue) => onSelectProject(newValue, { id, structureId })}
              inputProps={{
                'aria-label': `Select ${name} project`,
                id: `project_${id}-checkbox`,
              }}
            />
          ),
        },
        {
          cellKey: CELL_KEYS.NAME,
          cellStyles: styles.tableCell,
          content: <TitleTooltip titleLength={dashboardNameLength} title={name} variant="button" />,
        },
        {
          cellKey: CELL_KEYS.LAUNCH_DATE,
          cellStyles: styles.tableCell,
          content: (
            <Typography variant="body1" component="span">
              {formatDate(launchDate, 'DD.MM.YYYY')}
            </Typography>
          ),
          cellProps: { align: 'center' },
        },
        {
          cellKey: CELL_KEYS.PROJECT_SETTINGS,
          cellStyles: styles.tableCell,
          content: (
            <Label labelType={isPublic ? LABEL_TYPES.PUBLIC_PROJECT_SHORT : LABEL_TYPES.PRIVATE_PROJECT_SHORT} />
          ),
          cellProps: { align: 'center' },
        },
        {
          cellKey: CELL_KEYS.PROJECT_STATUS,
          cellStyles: styles.tableCell,
          content: <Status statusType={status} />,
          cellProps: { align: 'center' },
        },
      ],
    }));
  }, [filteredProjects, getResponsiveValueForCurrent, onSelectProject, selectedProjects, selectedProjectsGroup]);

  const onCancel = () => {
    if (props?.masterDashboardId) {
      dispatch(resetMasterDashboardProjectsAction());
    }
  };

  const onConfirm = () => {
    const masterDashboardId = props?.masterDashboardId ? `/${props.masterDashboardId}` : '';
    const path = `${routes.dashboardPortfolio}/${organisationId}/${routes.masterDashboardDetails}${masterDashboardId}`;

    dispatch(
      setMasterDashboardProjectsAction({
        group: selectedProjectsGroup,
        projects: selectedProjects,
      }),
    );
    closeDialog();

    if (!pathname.includes(routes.masterDashboardDetails)) {
      push(path);
    }
  };

  return (
    <Dialog
      id={dialogId}
      title="Add or Remove Projects"
      onClose={onCancel}
      confirmLabel="Generate Master Dashboard"
      isDisabledConfirm={selectedProjects.length < MIN_PROJECTS_COUNT}
      onConfirm={onConfirm}
      customStyles={{
        paperStyles: styles.dialogPaper,
        actionsStyles: styles.dialogActions,
      }}
    >
      <Stack spacing={4} alignItems="flex-start">
        {props?.masterDashboardName && (
          <Typography variant="subtitle1" component="p">
            {`Add or Remove Projects to the "${props.masterDashboardName}"`}
          </Typography>
        )}

        <SearchInput searchValue={searchValue} setSearchValue={setSearchValue} customStyles={styles.searchInput} />

        <Stack
          spacing={2}
          direction="row"
          justifyContent={selectedProjects.length ? 'space-between' : 'flex-end'}
          alignItems="center"
          width="100%"
        >
          {!!selectedProjects.length && (
            <Stack direction="row" alignItems="center" marginBottom={1}>
              <Typography variant="body2">Select:</Typography>

              <Button
                variant="text"
                size="small"
                onClick={onSelectAllProjects}
                sx={styles.selectAllButton}
                startIcon={
                  <Box sx={[styles.iconWrapper, isAllProjectsSelected && styles.iconWrapperSelected]}>
                    {isAllProjectsSelected && <Box component={ButtonConfirmIcon} />}
                  </Box>
                }
              >
                <Typography variant="body2" component="span">
                  Select All
                </Typography>
              </Button>
            </Stack>
          )}

          <Button variant="outlined" size="small" onClick={onClearSelection} sx={styles.clearSelectionButton}>
            Clear selection
          </Button>
        </Stack>

        <Box sx={styles.tableContainer}>
          <ColoredTable header={formatHeaders(projectTableHeaders)} rows={projectTableRows} />
        </Box>
      </Stack>
    </Dialog>
  );
};
